import { useState } from "react";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/odds";
import { useAuth } from "@/context/AuthContext";

export function BankrollPanel({ netProfit = 0 }) {
  const { user, setUser } = useAuth();
  const b = user.bankroll || { amount: 0, method: "kelly", unit_pct: 1 };
  const [form, setForm] = useState({ amount: b.amount || "", method: b.method, unit_pct: b.unit_pct });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/bankroll", { amount: Number(form.amount), method: form.method, unit_pct: Number(form.unit_pct) });
      setUser(data);
      toast.success("Bankroll saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-6" data-testid="bankroll-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Wallet size={20} className="text-cyan-400" /> Bankroll</h2>
          <p className="mt-1 text-xs text-slate-500">Stake suggestions appear in your slip based on each parlay's hit probability.</p>
        </div>
        {b.amount > 0 && (
          <div className="text-right">
            <p className="label-caps">Current bankroll</p>
            <p className="font-mono-num text-2xl font-bold text-white" data-testid="bankroll-current">{money(b.amount + netProfit)}</p>
            <p className="text-[11px] text-slate-500">Starting {money(b.amount)} · P/L <span className={netProfit >= 0 ? "text-emerald-400" : "text-red-400"}>{netProfit >= 0 ? "+" : ""}{money(netProfit)}</span></p>
          </div>
        )}
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
        <div>
          <label className="label-caps">Bankroll ($)</label>
          <input data-testid="bankroll-amount-input" type="number" min="0" className="input-dark font-mono-num mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="1000" />
        </div>
        <div>
          <label className="label-caps">Method</label>
          <div className="mt-1 flex rounded-lg border border-[#26334D] bg-[#0B0E14] p-1">
            {[["kelly", "¼ Kelly"], ["flat", "Flat units"]].map(([k, l]) => (
              <button key={k} data-testid={`bankroll-method-${k}`} onClick={() => setForm({ ...form, method: k })} className={`rounded-md px-3 py-1.5 text-xs font-bold ${form.method === k ? "bg-emerald-500 text-[#0B0E14]" : "text-slate-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        {form.method === "flat" && (
          <div>
            <label className="label-caps">Unit %</label>
            <input data-testid="bankroll-unit-input" type="number" step="0.25" min="0.25" max="5" className="input-dark font-mono-num mt-1 w-24" value={form.unit_pct} onChange={(e) => setForm({ ...form, unit_pct: e.target.value })} />
          </div>
        )}
        <button data-testid="bankroll-save-btn" onClick={save} disabled={busy || !form.amount} className="btn-primary">{busy ? "Saving…" : "Save"}</button>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">{form.method === "kelly" ? "Fractional Kelly stakes ¼ of the mathematically optimal fraction, capped at 5% of bankroll; parlays without positive expected value get a pass." : "Flat units: 1 unit = your unit % of bankroll; stake scales 0.5–3 units with the ticket's hit probability."}</p>
    </div>
  );
}
